import { supabase } from '../lib/supabase';
import { ProjectAttachment, TaskBoardTask } from '../types';

const BUCKET = 'assets';
const TASK_PREFIX = 'project-tasks';
const STICKY_PREFIX = 'project-sticky-notes';

const IMAGE_EXT = /\.(jpe?g|png|gif|webp|bmp|svg|avif)$/i;
const IMAGE_MIME = /^image\//;

export function safeProjectAttachments(
  attachments?: TaskBoardTask['attachments'] | null
): ProjectAttachment[] {
  if (!Array.isArray(attachments)) return [];
  return attachments.filter(a => a && typeof a.url === 'string');
}

export function isImageAttachment(a: ProjectAttachment): boolean {
  if (a.type && IMAGE_MIME.test(a.type)) return true;
  if (a.name && IMAGE_EXT.test(a.name)) return true;
  if (IMAGE_EXT.test(a.url.split('?')[0])) return true;
  return false;
}

export function getImageAttachments(attachments?: TaskBoardTask['attachments'] | null): ProjectAttachment[] {
  return safeProjectAttachments(attachments).filter(isImageAttachment);
}

export function getFileAttachments(attachments?: TaskBoardTask['attachments'] | null): ProjectAttachment[] {
  return safeProjectAttachments(attachments).filter(a => !isImageAttachment(a));
}

function sanitizeFileName(name: string): string {
  const ext = name.match(/\.[^.]+$/)?.[0]?.toLowerCase() || '';
  const base = name
    .replace(/\.[^.]+$/, '')
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/[^a-zA-Z0-9._-]+/g, '-')
    .replace(/-+/g, '-')
    .replace(/^-|-$/g, '') || 'file';
  return `${base}${ext}`;
}

async function uploadToAssets(path: string, file: File): Promise<ProjectAttachment> {
  const { error } = await supabase.storage.from(BUCKET).upload(path, file, {
    cacheControl: '3600',
    upsert: false,
  });
  if (error) throw error;

  const { data: { publicUrl } } = supabase.storage.from(BUCKET).getPublicUrl(path);

  return {
    url: publicUrl,
    path,
    name: file.name,
    type: file.type || undefined,
    size: file.size,
    created_at: new Date().toISOString(),
  };
}

export async function uploadProjectAttachment(
  taskId: string,
  file: File
): Promise<ProjectAttachment> {
  const safe = sanitizeFileName(file.name);
  const path = `${TASK_PREFIX}/${taskId}/${Date.now()}-${Math.random().toString(36).slice(2)}-${safe}`;
  return uploadToAssets(path, file);
}

export async function uploadStickyNoteAttachment(
  projectId: string,
  noteId: string,
  file: File
): Promise<ProjectAttachment> {
  const safe = sanitizeFileName(file.name);
  const path = `${STICKY_PREFIX}/${projectId}/${noteId}/${Date.now()}-${Math.random().toString(36).slice(2)}-${safe}`;
  return uploadToAssets(path, file);
}

export async function removeProjectAttachmentFromStorage(path?: string): Promise<void> {
  if (!path) return;
  const { error } = await supabase.storage.from(BUCKET).remove([path]);
  if (error) console.error('Error removing attachment from storage:', error);
}
